
import React, { useState, useMemo } from 'react';
import { ScheduleEvent, ScheduleMarker } from '../../../../models';
import {
    Trash2, RefreshCw, AlertTriangle, CheckSquare, Square, CheckCircle2, ChevronRight, X
} from 'lucide-react';

interface ScheduleRangeManagementProps {
    activeSchedules: ScheduleEvent[];
    recycleBinSchedules: ScheduleEvent[];
    scheduleMarkers: ScheduleMarker[];
    initialStartDate?: string;
    initialEndDate?: string;
    onDelete: (ids: string[]) => void;
    onRestore: (ids: string[]) => void;
    onPermanentDelete: (ids: string[]) => void;
    onDeleteMarker?: (id: string) => void;
    onSwitchToCreate: () => void;
}

type ListTab = 'active' | 'recycle';

const ScheduleRangeManagement: React.FC<ScheduleRangeManagementProps> = ({
    activeSchedules, recycleBinSchedules, scheduleMarkers,
    initialStartDate, initialEndDate,
    onDelete, onRestore, onPermanentDelete, onDeleteMarker, onSwitchToCreate
}) => {
    const [activeTab, setActiveTab] = useState<ListTab>('active');

    // Use props directly or default to today/next month if not provided (fallback)
    const rangeStart = initialStartDate || new Date().toISOString().split('T')[0];
    const rangeEnd = initialEndDate || new Date(new Date().setMonth(new Date().getMonth() + 1)).toISOString().split('T')[0];

    const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());

    const [activeList, recycleList] = useMemo(() => {
        // Fix: Parse YYYY-MM-DD as local midnight to match s.date (which is local timestamp)
        const [sy, sm, sd] = rangeStart.split('-').map(Number);
        const start = new Date(sy, sm - 1, sd).getTime();

        const [ey, em, ed] = rangeEnd.split('-').map(Number);
        const end = new Date(ey, em - 1, ed).getTime() + (24 * 60 * 60 * 1000) - 1; // End of day

        const filterFn = (s: ScheduleEvent) => s.date >= start && s.date <= end;

        const aList = activeSchedules.filter(filterFn).sort((a, b) => a.date - b.date);
        const rList = recycleBinSchedules.filter(filterFn).sort((a, b) => a.date - b.date);

        return [aList, rList];
    }, [activeSchedules, recycleBinSchedules, rangeStart, rangeEnd]);

    const filteredList = activeTab === 'active' ? activeList : recycleList;

    const filteredMarkers = useMemo(() => {
        return scheduleMarkers.filter(m => m.endDate >= rangeStart && m.startDate <= rangeEnd);
    }, [scheduleMarkers, rangeStart, rangeEnd]);

    const toggleSelection = (id: string) => {
        const newSet = new Set(selectedItems);
        if (newSet.has(id)) newSet.delete(id);
        else newSet.add(id);
        setSelectedItems(newSet);
    };

    const toggleAll = () => {
        if (selectedItems.size === filteredList.length) {
            setSelectedItems(new Set());
        } else {
            setSelectedItems(new Set(filteredList.map(item => item.id)));
        }
    };

    const getSubjectColor = (subject: string) => {
        const colors: Record<string, string> = {
            '국어': 'bg-rose-500', '수학': 'bg-blue-600', '영어': 'bg-indigo-600',
            '한국사': 'bg-amber-700', '사회': 'bg-orange-600', '과학': 'bg-emerald-600',
        };
        return colors[subject] || 'bg-slate-600';
    };

    return (
        <div className="flex-1 flex flex-col bg-slate-50/50">
            {/* Toolbar */}
            <div className="p-5 bg-white border-b border-slate-100 flex flex-col sm:flex-row gap-4 items-center justify-between">
                <div className="flex items-center gap-2 bg-slate-100 p-1.5 rounded-2xl">
                    <button
                        onClick={() => { setActiveTab('active'); setSelectedItems(new Set()); }}
                        className={`px-4 py-2 rounded-xl text-xs font-black transition-all ${activeTab === 'active' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
                    >
                        활성 일정 ({activeList.length})
                    </button>
                    <button
                        onClick={() => { setActiveTab('recycle'); setSelectedItems(new Set()); }}
                        className={`px-4 py-2 rounded-xl text-xs font-black transition-all ${activeTab === 'recycle' ? 'bg-white text-rose-500 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
                    >
                        휴지통 ({recycleList.length})
                    </button>
                </div>

                <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-slate-400 mr-2">조회 기간:</span>
                    <div className="flex items-center gap-2 bg-slate-50 px-3 py-2 rounded-xl border border-slate-200 text-xs font-bold text-slate-600">
                        <span>{rangeStart}</span>
                        <span className="text-slate-300">~</span>
                        <span>{rangeEnd}</span>
                    </div>
                </div>
            </div>

            {/* Actions Bar */}
            <div className="px-6 py-3 flex items-center justify-between bg-white/50 backdrop-blur-sm border-b border-slate-100">
                <div className="flex items-center gap-2">
                    <button onClick={toggleAll} className="flex items-center gap-2 text-xs font-bold text-slate-500 hover:text-slate-800 transition-colors">
                        {selectedItems.size > 0 && selectedItems.size === filteredList.length ? <CheckSquare size={16} className="text-indigo-600" /> : <Square size={16} />}
                        전체 선택 ({selectedItems.size})
                    </button>
                </div>

                <div className="flex items-center gap-2">
                    {activeTab === 'active' ? (
                        <>
                            {selectedItems.size > 0 && (
                                <button onClick={() => {
                                    if (confirm(`선택한 ${selectedItems.size}개의 일정을 휴지통으로 이동하시겠습니까?`)) {
                                        onDelete(Array.from(selectedItems));
                                        setSelectedItems(new Set());
                                    }
                                }} className="flex items-center gap-1.5 px-3 py-1.5 bg-rose-50 text-rose-600 rounded-lg text-xs font-black hover:bg-rose-100 transition-all">
                                    <Trash2 size={14} /> 선택 삭제
                                </button>
                            )}
                            <button onClick={onSwitchToCreate} className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-xs font-black hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-100">
                                <CheckCircle2 size={14} /> 일정 추가
                            </button>
                        </>
                    ) : (
                        <>
                            {selectedItems.size > 0 && (
                                <>
                                    <button onClick={() => {
                                        if (confirm(`선택한 ${selectedItems.size}개의 일정을 복구하시겠습니까?`)) {
                                            onRestore(Array.from(selectedItems));
                                            setSelectedItems(new Set());
                                        }
                                    }} className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-600 rounded-lg text-xs font-black hover:bg-emerald-100 transition-all">
                                        <RefreshCw size={14} /> 선택 복구
                                    </button>
                                    <button onClick={() => {
                                        if (confirm(`선택한 ${selectedItems.size}개의 일정을 영구 삭제하시겠습니까? (복구 불가)`)) {
                                            onPermanentDelete(Array.from(selectedItems));
                                            setSelectedItems(new Set());
                                        }
                                    }} className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 text-white rounded-lg text-xs font-black hover:bg-slate-900 transition-all">
                                        <AlertTriangle size={14} /> 영구 삭제
                                    </button>
                                </>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* List */}
            <div className="flex-1 p-4 sm:p-6">
                <div className="space-y-4">
                    {/* Markers List */}
                    {activeTab === 'active' && filteredMarkers.length > 0 && (
                        <div className="space-y-1 mb-4">
                            <h4 className="text-xs font-black text-slate-400 mb-2 px-1">특별 일정 ({filteredMarkers.length})</h4>
                            {filteredMarkers.map(m => (
                                <div key={m.id} className="flex items-center gap-3 p-3 bg-white border border-slate-100 rounded-xl shadow-sm group hover:border-indigo-100 relative">
                                    <div className={`w-1 h-8 rounded-full ${m.type === 'vacation' ? 'bg-rose-400' : m.type === 'exam' ? 'bg-amber-400' : 'bg-emerald-400'}`} />
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2">
                                            <span className="text-xs font-black text-slate-700">{m.title}</span>
                                            <span className="text-[10px] text-slate-400 px-1.5 py-0.5 bg-slate-50 rounded">
                                                {m.type === 'vacation' ? '방학' : m.type === 'exam' ? '시험' : m.type === 'makeup' ? '보강' : '기타'}
                                            </span>
                                        </div>
                                        <p className="text-[10px] text-slate-400 font-medium">
                                            {new Date(m.startDate).toLocaleDateString()} ~ {new Date(m.endDate).toLocaleDateString()}
                                        </p>
                                    </div>
                                    <button
                                        onClick={() => {
                                            if (confirm('이 특별 일정을 삭제하시겠습니까?')) {
                                                onDeleteMarker?.(m.id);
                                            }
                                        }}
                                        className="w-8 h-8 flex items-center justify-center text-slate-300 hover:text-rose-500 hover:bg-rose-50 rounded-lg transition-colors"
                                    >
                                        <X size={16} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="space-y-1">
                        {filteredList.length > 0 ? filteredList.map(item => (
                            <div key={item.id} onClick={() => toggleSelection(item.id)} className={`flex items-center gap-4 p-3 rounded-xl border transition-all cursor-pointer ${selectedItems.has(item.id) ? 'bg-indigo-50 border-indigo-200 ring-1 ring-indigo-200' : 'bg-white border-slate-100 hover:border-indigo-100 hover:shadow-sm'}`}>
                                <div className={selectedItems.has(item.id) ? 'text-indigo-600' : 'text-slate-300'}>
                                    {selectedItems.has(item.id) ? <CheckSquare size={18} /> : <Square size={18} />}
                                </div>
                                <div className="w-16 text-center">
                                    <p className="text-[10px] font-bold text-slate-400">{new Date(item.date).toLocaleDateString()}</p>
                                </div>
                                <div className={`px-2 py-0.5 rounded text-[9px] font-black text-white ${getSubjectColor(item.subject)}`}>
                                    {item.subject}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs font-bold text-slate-800 truncate">{item.bookTitle}</p>
                                    <p className="text-[10px] text-slate-500 truncate">{item.task}</p>
                                </div>
                                {item.status === 'completed' && <span className="text-[9px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded">완료됨</span>}
                                {activeTab === 'recycle' && item.deletedAt && (
                                    <span className="text-[9px] text-rose-400">삭제됨: {new Date(item.deletedAt).toLocaleDateString()}</span>
                                )}
                            </div>
                        )) : (
                            <div className="py-20 text-center text-slate-400 font-bold text-xs">
                                해당 기간에 일정이 없습니다.
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ScheduleRangeManagement;
